import React, { useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Dialog, hideDialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n/functions';
import { Switch } from '../../../base/react';
import { NOTIFICATION_TIMEOUT_TYPE, showErrorNotification, showNotification } from '../../../notifications';
import { setRequestingSubtitles, toggleLanguageSelectorDialog } from '../../actions.web';
import AbstractLanguageSelectorDialog, {
    IAbstractLanguageSelectorDialogProps
} from '../AbstractLanguageSelectorDialog';

import AvailableLanguages from './../../../transcribing/transcriber-langs.json';
import LanguageList from './LanguageList.web';

const useStyles = makeStyles()(theme => {
    return {
        paragraphWrapper: {
            fontSize: 14,
            margin: '10px 2px',
            color: '#000'
        },
        spanWrapper: {
            fontWeight: 700,
            cursor: 'pointer',
            margin: '0px 2px',
            color: theme.palette.link01,
            '&:hover': {
                backgroundColor: theme.palette.ui04,
                color: theme.palette.link01Hover
            }
        },
        switchWrapper: {
            display: 'flex',
            alignItems: 'baseline',
            fontSize: 16,
            width: '100%',
            justifyContent: 'space-between'
        },
        width65: {
            width: 65
        },
        mb25: {
            marginBottom: 24
        }
    };
});

const LanguageSelectorDialog = (
        props: IAbstractLanguageSelectorDialogProps
) => {
    const { dispatch, language, listItems, onLanguageSelected, subtitles, t, OngoingTranscriptionLanguage, showDefaultErrMsg,
        translationEnabled, isTranscribing, transcription, confSettings, isLocalModerator } = props;

    const { classes: styles } = useStyles();

    const onSelected = useCallback(
        (e: string) => {
            onLanguageSelected(e);
            dispatch(toggleLanguageSelectorDialog());
        },
        [ language ]
    );

    const onSourceLanguageClick = useCallback(() => {
        dispatch(showNotification({
            titleKey: 'transcribing.TranscriptionLanguageCantChange',
            descriptionKey: 'transcribing.TranscriptionLanguageCannotBeChangedOngoingCall',
            concatText: true,
            maxLines: 2
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));

        dispatch(hideDialog());
    }, []);

    const toggleSwitch = () => {

        if (transcription?.enabled && Boolean(language) === false) {
            if (confSettings?.TRANSCRIPTION_QUOTA_EXCEEDED === 1 ?? false) {
                if (isLocalModerator) {
                    dispatch(showErrorNotification(
                        {
                            descriptionKey: 'transcribing.transcriptionQuotaExceeded',
                            titleKey: 'transcribing.transcriptionQuotaExceededTitle'
                        }));
                } else {
                    return;
                }
            } else {
                dispatch(setRequestingSubtitles(true, Boolean(language), OngoingTranscriptionLanguage));
                setTimeout(() => {
                    dispatch(hideDialog());
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM);
            }
        } else {
            dispatch(setRequestingSubtitles(false, false, null));
            setTimeout(() => {
                dispatch(hideDialog());
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM);
        }
    };

    return (
        <Dialog
            cancelDisabled = { false }
            disableFooter = { true }
            hideCloseButton = { false }
            isModal = { false }
            ok = {{ hidden: true }}
            titleKey = 'transcribing.subtitlesTitle'
            width = 'small'>
            <p
                className = { styles.paragraphWrapper }>
                <span>{ t('transcribing.sourceLanguageDesc') }</span>
                <span
                    className = { styles.spanWrapper }
                    onClick = { onSourceLanguageClick } >
                    { AvailableLanguages[OngoingTranscriptionLanguage] }
                </span>
            </p>

            <div className = { styles.switchWrapper } >
                <p>{ t('transcribing.Transcriptions') }</p>
                <div className = { styles.width65 }>
                    <Switch
                        onValueChange = { () => toggleSwitch() }
                        value = { Boolean(language) === true ? isTranscribing || Boolean(language) : Boolean(language) } />
                </div>

            </div>

            <div className = { styles.mb25 }>
                <LanguageList
                    items = { listItems }
                    language = { language }
                    onLanguageSelected = { onSelected }
                    selectedLanguage = { subtitles }
                    switchEnabled = { Boolean(language) }
                    t = { t }
                    translationEnabled = { translationEnabled } />
            </div>

            {showDefaultErrMsg && <p>
                {t('transcribing.TranscriptionLangDefaultNote', {
                    language: AvailableLanguages[OngoingTranscriptionLanguage]
                })}
            </p>}
        </Dialog>
    );
};

/*
 * We apply AbstractLanguageSelector to fill in the AbstractProps common
 * to both the web and native implementations.
 */
// eslint-disable-next-line new-cap
export default translate(AbstractLanguageSelectorDialog(LanguageSelectorDialog)
);
